import React, { useState } from "react";
import { Link } from "react-router-dom";
import { useTranslation } from "react-i18next";
import { toast } from "sonner";
import { api, formatApiError } from "@/lib/apiClient";
import { EnvelopeSimple } from "@phosphor-icons/react";

export default function ForgotPassword() {
  const { t } = useTranslation();
  const [email, setEmail] = useState("");
  const [busy, setBusy] = useState(false);
  const [sent, setSent] = useState(false);
  const [err, setErr] = useState("");

  const submit = async (e) => {
    e.preventDefault();
    setErr("");
    setBusy(true);
    try {
      await api.post("/auth/forgot-password", { email });
      setSent(true);
      toast.success(t("forgot.sent"));
    } catch (e) {
      // Even on error, treat as sent to avoid email enumeration
      setSent(true);
      toast.success(t("forgot.sent"));
      const msg = formatApiError(e.response?.data?.detail);
      if (msg) setErr(msg);
    } finally {
      setBusy(false);
    }
  };

  return (
    <div data-testid="forgot-password-page" className="min-h-[70vh] flex items-center justify-center text-white px-6 py-16">
      <div className="w-full max-w-md">
        <div className="overline mb-3">{t("forgot.overline")}</div>
        <h1 className="font-display text-4xl mb-3 flex items-center gap-3">
          <EnvelopeSimple size={26} weight="duotone" className="text-[#E6C998]" />
          {t("forgot.title")}
        </h1>
        <p className="text-neutral-400 mb-8 text-sm leading-relaxed">
          {t("forgot.subtitle")}
        </p>

        {sent ? (
          <div
            data-testid="forgot-sent"
            className="border border-[#E6C998]/30 bg-[#141414] px-5 py-6 text-sm text-neutral-200"
          >
            {t("forgot.sent")}
            <div className="divider-gold my-6" />
            <Link
              to="/login"
              className="text-[#E6C998] gold-underline text-sm"
              data-testid="forgot-back-login"
            >
              ← {t("forgot.back")}
            </Link>
          </div>
        ) : (
          <form onSubmit={submit} className="space-y-5">
            <div>
              <label className="block text-xs uppercase tracking-wider text-neutral-400 mb-2">
                {t("forgot.email")}
              </label>
              <input
                data-testid="forgot-email"
                type="email"
                value={email}
                onChange={(e) => setEmail(e.target.value)}
                required
                className="w-full bg-black border border-white/20 text-white px-4 py-3 focus:border-[#E6C998] focus:outline-none focus:ring-1 focus:ring-[#E6C998]/40"
              />
            </div>
            {err && <div className="text-sm text-red-400" data-testid="forgot-error">{err}</div>}
            <button
              type="submit"
              data-testid="forgot-submit"
              disabled={busy}
              className="btn-gold w-full py-4 rounded-none text-sm disabled:opacity-60"
            >
              {busy ? t("forgot.submitting") : t("forgot.submit")}
            </button>
            <div className="text-xs text-neutral-500">
              <Link to="/login" data-testid="forgot-back-login" className="text-[#E6C998]">
                ← {t("forgot.back")}
              </Link>
            </div>
          </form>
        )}
      </div>
    </div>
  );
}
